import { computed, ref } from 'vue'
import { queueMonitorApi } from '@/api/queueMonitor'
import { withAsyncState } from '@/lib/asyncState'
import type { FailedJob, PaginationFilters, PaginationMeta, QueueStats } from '@/types/models'

// Singleton, following useRepoScans.ts's shape: shared state so the queue
// monitor view retains its list/pagination across navigation within a session.
const stats = ref<QueueStats | null>(null)
const failedJobs = ref<FailedJob[]>([])
const meta = ref<PaginationMeta | null>(null)
const loading = ref(false)
// Separate refs per fetch — `loadAll()` fires fetchStats/fetchFailedJobs
// concurrently on every poll tick, and a shared ref would let one call's
// `error.value = null` reset clobber the other's just-set error message.
const statsError = ref<string | null>(null)
const failedJobsError = ref<string | null>(null)
const error = computed(() => statsError.value ?? failedJobsError.value)

// The background poll and pagination clicks both write into `failedJobs`/
// `meta`, and either can resolve after the other. Every write is tagged with
// a token taken right before its request goes out; a write only applies if
// its token is still the newest one issued, so a poll response that lands
// after the user has already navigated elsewhere is silently dropped instead
// of overwriting the page the user is now looking at.
let failedJobsRequestToken = 0

function isStaleFailedJobsRequest(token: number): boolean {
  return token !== failedJobsRequestToken
}

export function useQueueMonitor() {
  async function fetchStats(): Promise<void> {
    await withAsyncState(statsError, 'Failed to load queue stats.', async () => {
      stats.value = await queueMonitorApi.stats()
    })
  }

  async function fetchFailedJobs(filters: PaginationFilters = {}): Promise<void> {
    const token = ++failedJobsRequestToken
    await withAsyncState(
      failedJobsError,
      'Failed to load failed jobs.',
      async () => {
        const page = await queueMonitorApi.listFailedJobs(filters)
        if (isStaleFailedJobsRequest(token)) return
        failedJobs.value = page.data
        meta.value = page.meta
      },
      loading,
    )
  }

  async function retryFailedJob(uuid: string, page = 1): Promise<PaginationMeta | null | undefined> {
    return withAsyncState(failedJobsError, 'Failed to retry job.', async () => {
      await queueMonitorApi.retryFailedJob(uuid)
      // Refetch the page instead of splicing locally, so `meta.total`/
      // `current_page` stay accurate rather than going stale until the next
      // poll — same reasoning as useRepoRules.deleteRule.
      const token = ++failedJobsRequestToken
      const result = await queueMonitorApi.listFailedJobs({ page })
      if (isStaleFailedJobsRequest(token)) return undefined
      failedJobs.value = result.data
      meta.value = result.meta
      return result.meta
    })
  }

  async function deleteFailedJob(uuid: string, page = 1): Promise<PaginationMeta | null | undefined> {
    return withAsyncState(failedJobsError, 'Failed to delete job.', async () => {
      await queueMonitorApi.deleteFailedJob(uuid)
      // Step back a page if deleting the last row left the requested page
      // empty, same as useRepoRules.deleteRule.
      const token = ++failedJobsRequestToken
      let result = await queueMonitorApi.listFailedJobs({ page })
      if (result.data.length === 0 && page > 1) {
        result = await queueMonitorApi.listFailedJobs({ page: page - 1 })
      }
      if (isStaleFailedJobsRequest(token)) return undefined
      failedJobs.value = result.data
      meta.value = result.meta
      return result.meta
    })
  }

  // Called on logout so the next session's queue monitor doesn't briefly show
  // the previous user's data before its own fetch resolves, and so a late
  // response from a request the previous session started can't land and
  // repopulate it.
  function reset(): void {
    failedJobsRequestToken += 1
    stats.value = null
    failedJobs.value = []
    meta.value = null
    loading.value = false
    statsError.value = null
    failedJobsError.value = null
  }

  return {
    stats,
    failedJobs,
    meta,
    loading,
    error,
    fetchStats,
    fetchFailedJobs,
    retryFailedJob,
    deleteFailedJob,
    reset,
  }
}
